import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Box, Plus, Edit, Trash2, Check, X, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Check types with sub-options
const DEFAULT_CHECK_TYPES = [
  { 
    id: 'address_check', 
    name: 'Address Verification', 
    description: 'Current & permanent address', 
    defaultPrice: 400,
    defaultTat: 3,
    hasSubOptions: true,
    subOptions: [
      { id: 'present', name: 'Present Address' },
      { id: 'permanent', name: 'Permanent Address' },
      { id: 'past_1', name: 'Past Address 1' },
      { id: 'past_2', name: 'Past Address 2' },
      { id: 'past_3', name: 'Past Address 3' },
      { id: 'past_4', name: 'Past Address 4' },
      { id: 'past_5', name: 'Past Address 5' }
    ]
  },
  { 
    id: 'criminal_court_check', 
    name: 'Criminal Court Check', 
    description: 'Criminal court record verification', 
    defaultPrice: 800, 
    defaultTat: 7,
    hasSubOptions: true,
    subOptions: [
      { id: 'present', name: 'Present' },
      { id: 'permanent', name: 'Permanent' },
      { id: 'past_1', name: 'Past 1' },
      { id: 'past_2', name: 'Past 2' },
      { id: 'past_3', name: 'Past 3' },
      { id: 'past_4', name: 'Past 4' },
      { id: 'past_5', name: 'Past 5' }
    ]
  },
  { 
    id: 'criminal_advocate_check', 
    name: 'Criminal Advocate Check', 
    description: 'Criminal advocate verification', 
    defaultPrice: 1000, 
    defaultTat: 7,
    hasSubOptions: true,
    subOptions: [
      { id: 'present', name: 'Present' },
      { id: 'permanent', name: 'Permanent' },
      { id: 'past_1', name: 'Past 1' },
      { id: 'past_2', name: 'Past 2' },
      { id: 'past_3', name: 'Past 3' },
      { id: 'past_4', name: 'Past 4' },
      { id: 'past_5', name: 'Past 5' }
    ]
  },
  { 
    id: 'id_check', 
    name: 'ID Verification', 
    description: 'Identity document verification', 
    defaultPrice: 300, 
    defaultTat: 2,
    hasSubOptions: true,
    subOptions: [
      { id: 'pan', name: 'PAN' },
      { id: 'aadhar', name: 'Aadhar' },
      { id: 'driving_license', name: 'Driving License' },
      { id: 'passport', name: 'Passport' },
      { id: 'others', name: 'Others' }
    ]
  },
  { 
    id: 'employment_check', 
    name: 'Employment Verification', 
    description: 'Employment history verification', 
    defaultPrice: 400,
    defaultTat: 5,
    hasSubOptions: true,
    subOptions: [
      { id: 'current', name: 'Current Employment' },
      { id: 'past_1', name: 'Past Employment 1' },
      { id: 'past_2', name: 'Past Employment 2' },
      { id: 'past_3', name: 'Past Employment 3' },
      { id: 'past_4', name: 'Past Employment 4' },
      { id: 'past_5', name: 'Past Employment 5' }
    ]
  },
  { 
    id: 'education_check', 
    name: 'Education Verification', 
    description: 'Degree & certificate verification', 
    defaultPrice: 750,
    defaultTat: 7,
    hasSubOptions: true,
    subOptions: [
      { id: 'highest', name: 'Highest Qualification' },
      { id: 'prior_ssc', name: 'Prior to Highest - SSC' },
      { id: 'prior_hsc', name: 'Prior to Highest - HSC' },
      { id: 'iti', name: 'ITI' },
      { id: 'diploma', name: 'Diploma' },
      { id: 'professional_course', name: 'Professional Course' }
    ]
  },
  { id: 'credit_check', name: 'Credit Check', description: 'Credit history verification', defaultPrice: 1500, defaultTat: 3 },
  { id: 'reference_check', name: 'Personal Reference Check', description: 'Personal references', defaultPrice: 500, defaultTat: 4 },
  { id: 'professional_reference', name: 'Professional Reference Check', description: 'Professional references', defaultPrice: 600, defaultTat: 4 },
  { id: 'drug_test', name: 'Drug Test', description: 'Drug screening test', defaultPrice: 1200, defaultTat: 5 },
  { id: 'global_database', name: 'Global Database Check', description: 'International watchlist screening', defaultPrice: 1000, defaultTat: 3 },
];

function PackagesTab() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePage, setShowCreatePage] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [customChecks, setCustomChecks] = useState([]);
  const [showAddCheckDialog, setShowAddCheckDialog] = useState(false);
  const [newCheckForm, setNewCheckForm] = useState({ 
    name: '', 
    description: '', 
    defaultTat: 5,
    hasSubOptions: false,
    subOptions: []
  });
  const [newSubOption, setNewSubOption] = useState('');
  
  // Form state with individual check pricing
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tat_days: '7',
    discount: 0, // Discount amount for billing purposes
    checks: {} // { check_id: { selected: bool, qty: number, price: number } }
  });

  // Initialize checks with default pricing and TAT
  const initializeChecks = () => {
    const checks = {};
    DEFAULT_CHECK_TYPES.forEach(check => {
      checks[check.id] = { 
        selected: false, 
        qty: 1, 
        price: check.defaultPrice,
        tat: check.defaultTat || 5,
        selectedSubOptions: check.hasSubOptions ? [] : null
      };
    });
    return checks;
  };

  // Combine default and custom checks
  const allCheckTypes = [...DEFAULT_CHECK_TYPES, ...customChecks];

  useEffect(() => {
    fetchPackages();
    fetchCustomChecks();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get('/packages');
      setPackages(response.data);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomChecks = async () => {
    try {
      const response = await axios.get('/check-types');
      setCustomChecks(response.data);
    } catch (error) {
      console.log('Custom checks not loaded');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      tat_days: '7',
      discount: 0,
      checks: initializeChecks()
    });
  };

  const openCreatePage = () => {
    resetForm();
    setShowCreatePage(true);
  };

  const openEditDialog = (pkg) => {
    const checks = initializeChecks();
    // Populate with package's existing checks
    pkg.checks?.forEach(c => {
      if (checks[c.check_type]) {
        checks[c.check_type] = { 
          selected: true, 
          qty: c.quantity || 1, 
          price: c.price || checks[c.check_type].price,
          tat: c.tat || checks[c.check_type].tat || 5,
          selectedSubOptions: c.sub_options || []
        };
      }
    });
    
    setFormData({
      name: pkg.name,
      description: pkg.description || '',
      tat_days: pkg.tat_days?.toString() || '7',
      discount: pkg.discount || 0,
      checks: checks
    });
    setShowEditDialog(pkg);
  };

  const handleCheckToggle = (checkId) => {
    setFormData(prev => {
      const checkType = [...DEFAULT_CHECK_TYPES, ...customChecks].find(c => c.id === checkId);
      // Get current check data or create default
      const currentCheck = prev.checks[checkId] || { 
        selected: false, 
        qty: 1, 
        price: checkType?.defaultPrice || 500,
        tat: checkType?.defaultTat || 5,
        selectedSubOptions: checkType?.hasSubOptions ? [] : null
      };
      
      return {
        ...prev,
        checks: {
          ...prev.checks,
          [checkId]: {
            ...currentCheck,
            selected: !currentCheck.selected
          }
        }
      };
    });
  };

  const handleCheckTatChange = (checkId, tat) => {
    setFormData(prev => {
      const currentCheck = prev.checks[checkId] || { selected: true, qty: 1, price: 500, tat: 5 };
      return {
        ...prev,
        checks: {
          ...prev.checks,
          [checkId]: {
            ...currentCheck,
            tat: parseInt(tat) || 1
          }
        }
      };
    });
  };

  const handleSubOptionToggle = (checkId, subOptionId) => {
    setFormData(prev => {
      const currentCheck = prev.checks[checkId] || { selected: true, qty: 1, price: 500, tat: 5, selectedSubOptions: [] };
      const currentSubOptions = currentCheck.selectedSubOptions || [];
      const newSubOptions = currentSubOptions.includes(subOptionId)
        ? currentSubOptions.filter(id => id !== subOptionId)
        : [...currentSubOptions, subOptionId];
      
      // Auto-update quantity based on number of selected sub-options
      const newQty = newSubOptions.length > 0 ? newSubOptions.length : 1;
      
      return {
        ...prev,
        checks: {
          ...prev.checks,
          [checkId]: {
            ...currentCheck,
            selectedSubOptions: newSubOptions,
            qty: newQty // Auto-set qty to number of selected sub-options
          }
        }
      };
    });
  };

  const handleCheckQtyChange = (checkId, qty) => {
    setFormData(prev => {
      const currentCheck = prev.checks[checkId] || { selected: true, qty: 1, price: 500, tat: 5 };
      return {
        ...prev,
        checks: {
          ...prev.checks,
          [checkId]: {
            ...currentCheck,
            qty: parseInt(qty) || 1
          }
        }
      };
    });
  };

  const handleCheckPriceChange = (checkId, price) => {
    setFormData(prev => {
      const currentCheck = prev.checks[checkId] || { selected: true, qty: 1, price: 500, tat: 5 };
      return {
        ...prev,
        checks: {
          ...prev.checks,
          [checkId]: {
            ...currentCheck,
            price: parseFloat(price) || 0
          }
        }
      };
    });
  };

  // Handle creating custom check types
  const handleCreateCustomCheck = async () => {
    if (!newCheckForm.name) {
      toast.error('Check name is required');
      return;
    }

    try {
      const checkId = newCheckForm.name.toLowerCase().replace(/\s+/g, '_');
      const newCheck = {
        id: checkId,
        name: newCheckForm.name,
        description: newCheckForm.description || '',
        defaultPrice: 500,
        defaultTat: newCheckForm.defaultTat || 5,
        hasSubOptions: newCheckForm.hasSubOptions && newCheckForm.subOptions.length > 0,
        subOptions: newCheckForm.subOptions || []
      };
      
      await axios.post('/check-types', newCheck);
      toast.success('Custom check type created');
      setCustomChecks([...customChecks, newCheck]);
      // Add to form checks
      setFormData(prev => ({
        ...prev,
        checks: {
          ...prev.checks,
          [checkId]: { 
            selected: false, 
            qty: 1, 
            price: 500, 
            tat: newCheck.defaultTat,
            selectedSubOptions: newCheck.hasSubOptions ? [] : null
          }
        }
      }));
      setNewCheckForm({ name: '', description: '', defaultTat: 5, hasSubOptions: false, subOptions: [] });
      setNewSubOption('');
      setShowAddCheckDialog(false);
    } catch (error) {
      // If backend doesn't support it, just add locally
      const checkId = newCheckForm.name.toLowerCase().replace(/\s+/g, '_');
      const newCheck = {
        id: checkId,
        name: newCheckForm.name,
        description: newCheckForm.description || '',
        defaultPrice: 500,
        defaultTat: newCheckForm.defaultTat || 5,
        hasSubOptions: newCheckForm.hasSubOptions && newCheckForm.subOptions.length > 0,
        subOptions: newCheckForm.subOptions || []
      };
      setCustomChecks([...customChecks, newCheck]);
      setFormData(prev => ({
        ...prev,
        checks: {
          ...prev.checks,
          [checkId]: { 
            selected: false, 
            qty: 1, 
            price: 500,
            tat: newCheck.defaultTat,
            selectedSubOptions: newCheck.hasSubOptions ? [] : null
          }
        }
      }));
      setNewCheckForm({ name: '', description: '', defaultTat: 5, hasSubOptions: false, subOptions: [] });
      setNewSubOption('');
      setShowAddCheckDialog(false);
      toast.success('Custom check type added');
    }
  };

  const addSubOptionToNewCheck = () => {
    if (!newSubOption.trim()) return;
    const subOptionId = newSubOption.toLowerCase().replace(/\s+/g, '_');
    setNewCheckForm(prev => ({
      ...prev,
      subOptions: [...prev.subOptions, { id: subOptionId, name: newSubOption.trim() }]
    }));
    setNewSubOption('');
  };

  const removeSubOptionFromNewCheck = (subOptionId) => {
    setNewCheckForm(prev => ({
      ...prev,
      subOptions: prev.subOptions.filter(s => s.id !== subOptionId)
    }));
  };

  // Calculate totals
  const getSelectedChecksCount = () => {
    return Object.values(formData.checks).filter(c => c?.selected).length;
  };

  const getTotalPrice = () => {
    return Object.entries(formData.checks)
      .filter(([_, c]) => c?.selected)
      .reduce((sum, [_, c]) => sum + (c.qty * c.price), 0);
  };

  // Get maximum TAT from selected checks
  const getMaxTat = () => {
    const selectedChecks = Object.values(formData.checks).filter(c => c?.selected);
    if (selectedChecks.length === 0) return 0;
    return Math.max(...selectedChecks.map(c => c.tat || 0));
  };

  // Get final price after discount (for billing)
  const getFinalPrice = () => {
    const subtotal = getTotalPrice();
    const discount = parseFloat(formData.discount) || 0;
    return Math.max(0, subtotal - discount);
  };

  const handleCreatePackage = async () => {
    if (!formData.name || getSelectedChecksCount() === 0) {
      toast.error('Please fill all required fields and select at least one check');
      return;
    }

    try {
      const selectedChecks = Object.entries(formData.checks)
        .filter(([_, c]) => c?.selected)
        .map(([checkType, c]) => ({ 
          check_type: checkType, 
          quantity: c.qty, 
          price: c.price,
          tat: c.tat,
          sub_options: c.selectedSubOptions || []
        }));

      const payload = {
        name: formData.name,
        description: formData.description,
        price: getFinalPrice(), // Final price after discount for billing
        subtotal: getTotalPrice(), // Original total before discount
        discount: parseFloat(formData.discount) || 0,
        tat_days: getMaxTat(), // Auto-calculated from max check TAT
        checks: selectedChecks
      };
      
      await axios.post('/packages', payload);
      toast.success('Package created successfully');
      setShowCreatePage(false);
      fetchPackages();
    } catch (error) {
      toast.error('Failed to create package');
    }
  };

  const handleUpdatePackage = async () => {
    if (!formData.name || getSelectedChecksCount() === 0) {
      toast.error('Please fill all required fields and select at least one check');
      return;
    }

    try {
      const selectedChecks = Object.entries(formData.checks)
        .filter(([_, c]) => c?.selected)
        .map(([checkType, c]) => ({ 
          check_type: checkType, 
          quantity: c.qty, 
          price: c.price,
          tat: c.tat,
          sub_options: c.selectedSubOptions || []
        }));

      const payload = {
        name: formData.name,
        description: formData.description,
        price: getFinalPrice(), // Final price after discount for billing
        subtotal: getTotalPrice(), // Original total before discount
        discount: parseFloat(formData.discount) || 0,
        tat_days: getMaxTat(), // Auto-calculated from max check TAT
        checks: selectedChecks
      };
      
      await axios.put(`/packages/${showEditDialog.id}`, payload);
      toast.success('Package updated successfully');
      setShowEditDialog(null);
      fetchPackages();
    } catch (error) {
      toast.error('Failed to update package');
    }
  };

  const handleDeletePackage = async (packageId) => {
    try {
      await axios.delete(`/packages/${packageId}`);
      toast.success('Package deleted successfully');
      setShowDeleteConfirm(null);
      fetchPackages();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const getCheckName = (checkType) => {
    const check = allCheckTypes.find(c => c.id === checkType);
    return check ? check.name : checkType.replace(/_/g, ' ').toUpperCase();
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  // Create Package Full Page View
  if (showCreatePage) {
    return (
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-slate-900">Create Package</h1>
          <Button variant="ghost" onClick={() => setShowCreatePage(false)} className="text-slate-600">
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        </div>

        <div className="flex gap-6">
          {/* Left Column - Form */}
          <div className="flex-1 space-y-6">
            {/* Package Details Card */}
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold">Package Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Package Name *</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Basic Verification"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea 
                    value={formData.description} 
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Optional description"
                    rows={2}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Select Checks & Pricing Card */}
            <Card className="border-slate-200">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold">Select Checks & Pricing</CardTitle>
                    <p className="text-sm text-slate-500">Choose checks and set individual pricing</p>
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowAddCheckDialog(true)}
                    className="text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add New Check Type
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[...DEFAULT_CHECK_TYPES, ...customChecks].map((check) => {
                    const checkData = formData.checks[check.id] || { 
                      selected: false, 
                      qty: 1, 
                      price: check.defaultPrice || 500,
                      tat: check.defaultTat || 5,
                      selectedSubOptions: check.hasSubOptions ? [] : null
                    };
                    const lineTotal = checkData.selected ? checkData.qty * checkData.price : 0;
                    
                    return (
                      <div 
                        key={check.id}
                        className={`p-4 border rounded-lg transition-colors ${
                          checkData.selected 
                            ? 'border-blue-300 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        {/* Main Check Row */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Checkbox 
                              checked={checkData.selected}
                              onCheckedChange={() => handleCheckToggle(check.id)}
                              className="data-[state=checked]:bg-blue-600"
                            />
                            <div>
                              <span className="font-medium text-slate-800">{check.name}</span>
                              {check.description && (
                                <p className="text-xs text-slate-500">{check.description}</p>
                              )}
                            </div>
                          </div>
                          
                          {checkData.selected && (
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-500">Qty:</span>
                                <Input 
                                  type="number" 
                                  value={checkData.qty}
                                  onChange={(e) => handleCheckQtyChange(check.id, e.target.value)}
                                  className="w-14 h-7 text-center text-sm"
                                  min="1"
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-500">₹</span>
                                <Input 
                                  type="number" 
                                  value={checkData.price}
                                  onChange={(e) => handleCheckPriceChange(check.id, e.target.value)}
                                  className="w-20 h-7 text-center text-sm"
                                  min="0"
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-500">TAT:</span>
                                <Input 
                                  type="number" 
                                  value={checkData.tat || check.defaultTat || 5}
                                  onChange={(e) => handleCheckTatChange(check.id, e.target.value)}
                                  className="w-14 h-7 text-center text-sm"
                                  min="1"
                                />
                                <span className="text-xs text-slate-500">days</span>
                              </div>
                              <Badge className="bg-slate-100 text-slate-700 font-semibold min-w-[70px] justify-center text-xs">
                                ₹{lineTotal.toLocaleString()}
                              </Badge>
                            </div>
                          )}
                        </div>

                        {/* Sub-Options Row */}
                        {checkData.selected && check.hasSubOptions && check.subOptions?.length > 0 && (
                          <div className="mt-3 ml-8 pl-4 border-l-2 border-blue-200">
                            <p className="text-xs text-slate-500 mb-2">Select options:</p>
                            <div className="flex flex-wrap gap-2">
                              {check.subOptions.map((subOption) => (
                                <label 
                                  key={subOption.id}
                                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer transition-colors text-sm ${
                                    (checkData.selectedSubOptions || []).includes(subOption.id)
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-white border border-slate-300 text-slate-700 hover:border-blue-400'
                                  }`}
                                >
                                  <Checkbox 
                                    checked={(checkData.selectedSubOptions || []).includes(subOption.id)}
                                    onCheckedChange={() => handleSubOptionToggle(check.id, subOption.id)}
                                    className="w-3.5 h-3.5 data-[state=checked]:bg-white data-[state=checked]:text-blue-600 border-current"
                                  />
                                  {subOption.name}
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Summary */}
          <div className="w-72">
            <Card className="border-slate-200 sticky top-6">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold">Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500">Package Name</p>
                  <p className="font-medium text-slate-800">{formData.name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Selected Checks</p>
                  <p className="font-medium text-slate-800">{getSelectedChecksCount()}</p>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700 font-medium">Overall TAT (Auto-calculated)</p>
                  <p className="text-2xl font-bold text-amber-800">{getMaxTat()} days</p>
                  <p className="text-xs text-amber-600">Based on highest check TAT</p>
                </div>
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-500">Subtotal</p>
                  <p className="text-xl font-semibold text-slate-700">₹{getTotalPrice().toLocaleString()}</p>
                </div>
                {/* Discount Input */}
                <div>
                  <Label className="text-sm text-slate-500">Discount Amount (₹)</Label>
                  <Input 
                    type="number"
                    value={formData.discount}
                    onChange={(e) => setFormData({...formData, discount: e.target.value})}
                    placeholder="0"
                    min="0"
                    max={getTotalPrice()}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-400 mt-1">For billing purposes only</p>
                </div>
                {/* Final Price */}
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-500">Total Price</p>
                  <p className="text-3xl font-bold text-slate-900">₹{getFinalPrice().toLocaleString()}</p>
                  <p className="text-sm text-slate-500">per employee</p>
                  {parseFloat(formData.discount) > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      You save ₹{parseFloat(formData.discount).toLocaleString()}
                    </p>
                  )}
                </div>
                <Button 
                  onClick={handleCreatePackage} 
                  className="w-full bg-slate-900 hover:bg-slate-800 mt-4"
                  disabled={!formData.name || getSelectedChecksCount() === 0}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Create Package
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Add New Check Type Dialog - for Create Page */}
        <Dialog open={showAddCheckDialog} onOpenChange={setShowAddCheckDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Check Type</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Check Name *</Label>
                  <Input 
                    value={newCheckForm.name} 
                    onChange={(e) => setNewCheckForm({...newCheckForm, name: e.target.value})}
                    placeholder="e.g., Social Media Check"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Default TAT (Days)</Label>
                  <Input 
                    type="number"
                    value={newCheckForm.defaultTat} 
                    onChange={(e) => setNewCheckForm({...newCheckForm, defaultTat: parseInt(e.target.value) || 5})}
                    placeholder="5"
                    min="1"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={newCheckForm.description} 
                  onChange={(e) => setNewCheckForm({...newCheckForm, description: e.target.value})}
                  placeholder="Brief description of this check type"
                  rows={2}
                  className="mt-1"
                />
              </div>
              
              {/* Sub-Options Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium">Sub-Options (Optional)</Label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox 
                      checked={newCheckForm.hasSubOptions}
                      onCheckedChange={(checked) => setNewCheckForm({...newCheckForm, hasSubOptions: checked})}
                    />
                    Enable sub-options
                  </label>
                </div>
                
                {newCheckForm.hasSubOptions && (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Input 
                        value={newSubOption}
                        onChange={(e) => setNewSubOption(e.target.value)}
                        placeholder="e.g., Current, Past, Highest"
                        className="flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSubOptionToNewCheck())}
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={addSubOptionToNewCheck}
                        disabled={!newSubOption.trim()}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {newCheckForm.subOptions.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {newCheckForm.subOptions.map((opt) => (
                          <Badge 
                            key={opt.id} 
                            className="bg-blue-100 text-blue-800 px-3 py-1 flex items-center gap-2"
                          >
                            {opt.name}
                            <button 
                              type="button"
                              onClick={() => removeSubOptionFromNewCheck(opt.id)}
                              className="hover:text-blue-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <p className="text-xs text-slate-500">
                      Examples: Address → Present, Past | Education → Highest, Prior to Highest | Employment → Current, Past
                    </p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowAddCheckDialog(false);
                setNewCheckForm({ name: '', description: '', defaultTat: 5, hasSubOptions: false, subOptions: [] });
                setNewSubOption('');
              }}>Cancel</Button>
              <Button onClick={handleCreateCustomCheck} className="bg-slate-900 hover:bg-slate-800">
                Add Check Type
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main Packages List View
  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Packages</h1>
          <p className="text-slate-600">Manage verification packages and pricing</p>
        </div>
        <Button onClick={openCreatePage} className="bg-slate-900 hover:bg-slate-800" data-testid="create-package-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Package
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages.map((pkg) => (
          <Card key={pkg.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Box className="w-5 h-5 text-slate-600" />
                  {pkg.name}
                </CardTitle>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(pkg)} data-testid={`edit-package-${pkg.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(pkg)} className="text-rose-600 hover:text-rose-700" data-testid={`delete-package-${pkg.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-bold text-slate-900">₹{pkg.price?.toLocaleString()}</span>
                <span className="text-sm text-slate-500">/ employee</span>
              </div>
              {pkg.discount > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-slate-400 line-through">₹{pkg.subtotal?.toLocaleString()}</span>
                  <Badge className="bg-green-100 text-green-700 text-xs">Save ₹{pkg.discount?.toLocaleString()}</Badge>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                <Badge variant="outline" className="font-normal">{pkg.tat_days} days TAT</Badge>
                <Badge variant="outline" className="font-normal">{pkg.checks?.length || 0} checks</Badge>
              </div>
              {pkg.description && (
                <p className="text-sm text-slate-500 mb-4">{pkg.description}</p>
              )}
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-500 uppercase">Included Checks:</p>
                <div className="flex flex-wrap gap-1">
                  {pkg.checks?.slice(0, 4).map((check, idx) => (
                    <Badge key={idx} className="bg-slate-100 text-slate-700 text-xs font-normal">
                      {getCheckName(check.check_type)}
                    </Badge>
                  ))}
                  {pkg.checks?.length > 4 && (
                    <Badge className="bg-slate-100 text-slate-700 text-xs font-normal">
                      +{pkg.checks.length - 4} more
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {packages.length === 0 && (
          <Card className="border-slate-200 col-span-full">
            <CardContent className="text-center py-12">
              <Box className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">No packages created yet</p>
              <Button onClick={openCreatePage} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Package
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Package Dialog */}
      <Dialog open={!!showEditDialog} onOpenChange={(open) => !open && setShowEditDialog(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Package</DialogTitle>
          </DialogHeader>
          <div className="flex gap-6 py-4">
            {/* Form */}
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Package Name *</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>TAT (Days) *</Label>
                  <Input 
                    type="number" 
                    value={formData.tat_days} 
                    onChange={(e) => setFormData({...formData, tat_days: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label className="mb-3 block">Select Checks & Pricing</Label>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {DEFAULT_CHECK_TYPES.map((check) => {
                    const checkData = formData.checks[check.id] || { selected: false, qty: 1, price: check.defaultPrice };
                    const lineTotal = checkData.selected ? checkData.qty * checkData.price : 0;
                    
                    return (
                      <div 
                        key={check.id}
                        className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                          checkData.selected 
                            ? 'border-blue-300 bg-blue-50' 
                            : 'border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox 
                            checked={checkData.selected}
                            onCheckedChange={() => handleCheckToggle(check.id)}
                            className="data-[state=checked]:bg-blue-600"
                          />
                          <span className="font-medium text-sm">{check.name}</span>
                        </div>
                        
                        {checkData.selected && (
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-slate-500">Qty:</span>
                              <Input 
                                type="number" 
                                value={checkData.qty}
                                onChange={(e) => handleCheckQtyChange(check.id, e.target.value)}
                                className="w-14 h-7 text-center text-sm"
                                min="1"
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-slate-500">₹</span>
                              <Input 
                                type="number" 
                                value={checkData.price}
                                onChange={(e) => handleCheckPriceChange(check.id, e.target.value)}
                                className="w-20 h-7 text-center text-sm"
                                min="0"
                              />
                            </div>
                            <Badge className="bg-slate-100 text-slate-700 text-xs font-semibold">
                              ₹{lineTotal.toLocaleString()}
                            </Badge>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="w-56 bg-slate-50 rounded-lg p-4">
              <h3 className="font-semibold mb-4">Summary</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-slate-500">Package Name</p>
                  <p className="font-medium">{formData.name || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">TAT</p>
                  <p className="font-medium">{formData.tat_days} days</p>
                </div>
                <div>
                  <p className="text-slate-500">Selected Checks</p>
                  <p className="font-medium">{getSelectedChecksCount()}</p>
                </div>
                <div className="pt-3 border-t">
                  <p className="text-slate-500">Subtotal</p>
                  <p className="text-lg font-semibold">₹{getTotalPrice().toLocaleString()}</p>
                </div>
                {/* Discount Input */}
                <div>
                  <Label className="text-xs text-slate-500">Discount (₹)</Label>
                  <Input 
                    type="number"
                    value={formData.discount}
                    onChange={(e) => setFormData({...formData, discount: e.target.value})}
                    placeholder="0"
                    min="0"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div className="pt-3 border-t">
                  <p className="text-slate-500">Total Price</p>
                  <p className="text-2xl font-bold">₹{getFinalPrice().toLocaleString()}</p>
                  <p className="text-xs text-slate-500">per employee</p>
                  {parseFloat(formData.discount) > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      Discount: ₹{parseFloat(formData.discount).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(null)}>Cancel</Button>
            <Button onClick={handleUpdatePackage} className="bg-slate-900 hover:bg-slate-800">
              <Check className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={(open) => !open && setShowDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Package</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 py-4">
            Are you sure you want to delete <strong>{showDeleteConfirm?.name}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
            <Button 
              onClick={() => handleDeletePackage(showDeleteConfirm?.id)} 
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Check Type Dialog */}
      <Dialog open={showAddCheckDialog} onOpenChange={setShowAddCheckDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Check Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Check Name *</Label>
              <Input 
                value={newCheckForm.name} 
                onChange={(e) => setNewCheckForm({...newCheckForm, name: e.target.value})}
                placeholder="e.g., Social Media Check"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={newCheckForm.description} 
                onChange={(e) => setNewCheckForm({...newCheckForm, description: e.target.value})}
                placeholder="Brief description of this check type"
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCheckDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCustomCheck} className="bg-slate-900 hover:bg-slate-800">
              Add Check Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PackagesTab;
